import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useLazyQuery, gql } from "@apollo/client";
import {
  Card,
  Button,
  Select,
  Modal,
  Spin,
  message,
  Checkbox,
  InputNumber,
  Input,
} from "antd";
import { ExclamationCircleOutlined, EditOutlined } from "@ant-design/icons";

const GET_ORDER_INVENTORY = gql`
  query getOrderInventory($data: OrderInventoryInput!) {
    getOrderInventory(data: $data) {
      status
      message
      inventories {
        id
        orderId
        serviceId
        inventoryId
        parts
        serviceName
        categoryName
        serviceCount
        scanned
        isScanRequired
        scannedInventoryId
        productSelectionMode
        alternateGroupId
        reversed
        reversedAt
        reversedReason
        isManualEntry
        manualCount
        recipeDefaultCount
        extraCost
        deductionGap
        deductionGapDetail
        scannedInventoryDetails {
          status
          quantity
          usage
          employeeId
          assignedDate
          verifiedDate
        }
      }
    }
  }
`;

const GET_CURRENT_INVENTORY = gql`
  mutation getCurrentInventory($data: EmployeeInventoryInput) {
    getCurrentInventory(data: $data) {
      status
      message
      inventoryList {
        id
        name
        inventoryCategory {
          id
          name
        }
      }
    }
  }
`;

const UPDATE_ORDER_INVENTORY = gql`
  mutation updateOrderInvenotry($data: UpdateOrderInventoryInput!) {
    updateOrderInvenotry(data: $data) {
      status
      message
      shortageList {
        name
        partsLeft
      }
    }
  }
`;

// NEW: mutation to generate inventories for an order
const GENERATE_ORDER_INVENTORIES = gql`
  mutation generateOrderInventories($data: generateOrderInventoriesInput!) {
    generateOrderInventories(data: $data) {
      status
      message
      inventories {
        id
        orderId
        serviceId
        inventoryId
        parts
        serviceName
        categoryName
        serviceCount
        scanned
        isManualEntry
        manualCount
      }
    }
  }
`;

// NEW: fetch an order's services (incl. package selection + inventory-generation info)
// so we can ask admin the same questions the employee app asks at start-service time.
const GET_ORDER_SERVICES_FOR_INVENTORY = gql`
  query getOrderServicesForInventory($data: generateOrderInventoriesInput!) {
    getOrderServicesForInventory(data: $data) {
      status
      message
      orderServices {
        id
        serviceId
        count
        serviceDetails {
          id
          name
          isPackage
          selectionCount
          inventoryGenerationMode
          packageChildServices {
            id
            name
            price
            time
          }
        }
      }
    }
  }
`;

// NEW: categories where the employee/admin manually enters a count (e.g. Self Disposable Packet)
const GET_MANUAL_COUNT_CATEGORIES = gql`
  query getInventoryCategoryForManualCount {
    getInventoryCategory {
      status
      message
      categories {
        id
        name
        allowManualCount
      }
    }
  }
`;

// Same category list, but with `parts` so we can restrict the "+ Add missing
// item" picker to single-use categories - the same rule the backend
// (addAdHocOrderInventoryItems) enforces.
const GET_ADD_ITEM_CATEGORIES = gql`
  query getInventoryCategoryForAddItem {
    getInventoryCategory {
      status
      message
      categories {
        id
        name
        allowManualCount
        parts
      }
    }
  }
`;

// NEW: verify inventory mutation — backend: verifyInventory(data: ID!): BookingModifiedResponse!
const VERIFY_INVENTORY = gql`
  mutation verifyInventory($data: ID!) {
    verifyInventory(data: $data) {
      status
      message
      order {
        id
        price
        totalPrice
        status
        address
        bookingDate
        timeSlot
        paymentStatus
        pincode
        paymentType
        verificationStatus
        femaleTimeSlot
        bookingId
        bookingType
        adminPriceAdjustment
        adminPriceAdjustmentReason
        couponDiscount
        codCharges
        couponCode
        orderDetail {
          id
          name
          serviceId
          price
          specialPrice
          firstTimePrice
          cutPrice
          count
          type
        }
        employee {
          id
          fullName
        }
        user {
          id
          fullName
          contactNumber
        }
      }
    }
  }
`;

const ADMIN_REVERSE_ORDER_INVENTORY = gql`
  mutation adminReverseOrderInventory($data: AdminReverseOrderInventoryInput!) {
    adminReverseOrderInventory(data: $data) {
      status
      message
      reversedCount
    }
  }
`;

const ADMIN_RESTORE_ORDER_INVENTORY_ITEM = gql`
  mutation adminRestoreOrderInventoryItem($data: AdminRestoreOrderInventoryItemInput!) {
    adminRestoreOrderInventoryItem(data: $data) {
      status
      message
    }
  }
`;

const ADMIN_UPDATE_ORDER_INVENTORY_PARTS = gql`
  mutation adminUpdateOrderInventoryParts($data: AdminUpdateOrderInventoryPartsInput!) {
    adminUpdateOrderInventoryParts(data: $data) {
      status
      message
    }
  }
`;

const ADMIN_ADD_ORDER_INVENTORY_ITEM = gql`
  mutation adminAddOrderInventoryItem($data: AdminAddOrderInventoryItemInput!) {
    adminAddOrderInventoryItem(data: $data) {
      status
      message
    }
  }
`;

const REVERSED_REASON_LABELS = {
  EMPLOYEE_REMOVED_PRESTART: "Removed by employee",
  EMPLOYEE_REMOVED_AFTER_START: "Removed by employee after start",
  EMPLOYEE_REMOVED_ADDON: "Removed by employee (add-on review)",
  ALTERNATE_NOT_USED: "Not used (other option used)",
  SERVICE_NOT_COMPLETED: "Not used (service not done)",
};

const OrderInventoryPage = ({ orderId, employeeId, editingData, onUpdate }) => {
  const [groupedData, setGroupedData] = useState({});
  const [allInventories, setAllInventories] = useState([]);
  const [availableInventories, setAvailableInventories] = useState([]);
  const [allScanned, setAllScanned] = useState(false);
  const [inventoriesEmpty, setInventoriesEmpty] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Package-selection step (mirrors the employee app's start-service flow)
  const [packagesNeedingSelection, setPackagesNeedingSelection] = useState([]);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [packageSelections, setPackageSelections] = useState({}); // orderServiceId -> [serviceId]
  const [pendingPackagePayload, setPendingPackagePayload] = useState([]);

  // Manual-count step (e.g. Self Disposable Packet - "how many people were served?")
  const [manualCountCategories, setManualCountCategories] = useState([]);
  const [manualCountModalOpen, setManualCountModalOpen] = useState(false);
  const [manualCounts, setManualCounts] = useState({}); // categoryId -> count

  // --- Edit mode (admin corrections) ---
  const [editMode, setEditMode] = useState(false);
  const [editedParts, setEditedParts] = useState({}); // orderInventoryId -> new parts
  const [toggledMarks, setToggledMarks] = useState({}); // orderInventoryId -> "REVERSE" | "RESTORE"
  const [stagedAdds, setStagedAdds] = useState([]); // { tempId, serviceId, serviceLabel, categoryId, categoryName, count }
  const [addItemDrafts, setAddItemDrafts] = useState({}); // serviceKey -> { categoryId, count }
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [batchReason, setBatchReason] = useState("");
  const [submittingEdits, setSubmittingEdits] = useState(false);
  const [tempIdCounter, setTempIdCounter] = useState(0);

  const { loading, data, refetch } = useQuery(GET_ORDER_INVENTORY, {
    variables: { data: { orderId } },
    fetchPolicy: "network-only",
  });

  const { data: addItemCategoryData } = useQuery(GET_ADD_ITEM_CATEGORIES, {
    fetchPolicy: "cache-first",
  });

  const [getCurrentInventory] = useMutation(GET_CURRENT_INVENTORY);
  const [updateOrderInventory] = useMutation(UPDATE_ORDER_INVENTORY);
  const [generateOrderInventories] = useMutation(GENERATE_ORDER_INVENTORIES);
  const [verifyInventory, { loading: verifying }] =
    useMutation(VERIFY_INVENTORY);
  const [adminReverseOrderInventory] = useMutation(ADMIN_REVERSE_ORDER_INVENTORY);
  const [adminRestoreOrderInventoryItem] = useMutation(ADMIN_RESTORE_ORDER_INVENTORY_ITEM);
  const [adminUpdateOrderInventoryParts] = useMutation(ADMIN_UPDATE_ORDER_INVENTORY_PARTS);
  const [adminAddOrderInventoryItem] = useMutation(ADMIN_ADD_ORDER_INVENTORY_ITEM);
  const [fetchOrderServicesForInventory] = useLazyQuery(
    GET_ORDER_SERVICES_FOR_INVENTORY,
    { fetchPolicy: "network-only" }
  );
  const [fetchManualCountCategories] = useLazyQuery(
    GET_MANUAL_COUNT_CATEGORIES,
    { fetchPolicy: "network-only" }
  );

  const addItemCategories = (addItemCategoryData?.getInventoryCategory?.categories || [])
    .filter((c) => c.allowManualCount && (c.parts || 1) <= 1);

  const groupInventories = (inventories) => {
    const grouped = {};
    inventories.forEach((inv) => {
      const key = `${inv.serviceName} - ${inv.serviceCount}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(inv);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) =>
        (a.categoryName || "").localeCompare(b.categoryName || "")
      );
    });
    return Object.fromEntries(
      Object.entries(grouped).sort(([a], [b]) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      )
    );
  };

  useEffect(() => {
    if (data?.getOrderInventory) {
      const resp = data.getOrderInventory;
      const inventories = resp.inventories || [];

      setInventoriesEmpty(inventories.length === 0);
      setAllInventories(inventories);

      if (inventories.length > 0) {
        setGroupedData(groupInventories(inventories));
        // Auto-cut items (isScanRequired=false) count as done regardless of scanned flag
        // — the backend handles them automatically in verifyInventory
        const allDone = inventories.every(
          (inv) => inv.scanned || inv.isScanRequired === false || inv.reversed
        );
        setAllScanned(allDone);
      } else {
        setGroupedData({});
        setAllScanned(false);
      }
    }
  }, [data]);

  useEffect(() => {
    const loadInventories = async () => {
      try {
        const res = await getCurrentInventory({
          variables: { data: { id: employeeId } },
        });
        setAvailableInventories(
          res.data?.getCurrentInventory?.inventoryList || []
        );
      } catch (err) {
        console.error("Failed to load current inventory", err);
        setAvailableInventories([]);
      }
    };
    if (employeeId) loadInventories();
  }, [employeeId, getCurrentInventory]);

  // Per-category rollup: how many parts the service recipe expected to be
  // deducted vs. how many were actually scanned/auto-cut off the employee's
  // stock, so a mismatch is visible without counting rows by hand.
  const inventoryByCategory = useMemo(() => {
    const map = {};
    allInventories.forEach((inv) => {
      const key = inv.inventoryId || inv.categoryName;
      if (!map[key]) {
        map[key] = {
          categoryId: key,
          categoryName: inv.categoryName || "Unknown",
          expectedParts: 0,
          actualParts: 0,
          extraCost: 0,
        };
      }
      const parts = Number(inv.parts) || 0;
      if (!inv.reversed) {
        map[key].expectedParts += parts;
      }
      if (inv.scanned && !inv.reversed) {
        map[key].actualParts += parts;
      }
      if (inv.extraCost && !inv.reversed) {
        map[key].extraCost += Number(inv.extraCost);
      }
    });
    return Object.values(map).sort((a, b) =>
      a.categoryName.localeCompare(b.categoryName)
    );
  }, [allInventories]);

  const handleInventorySelect = async (
    inv,
    selectedName,
    forceSkip = false
  ) => {
    try {
      const result = await updateOrderInventory({
        variables: {
          data: {
            id: inv.id,
            categoryId: inv.inventoryId,
            name: selectedName,
            parts: inv.parts,
            forceSkip,
            employeeId,
          },
        },
      });

      const res = result.data.updateOrderInvenotry;
      if (res.status === "ERROR") {
        Modal.error({
          title: "Error",
          content: res.message,
        });
      } else if (res.status === "WARNING") {
        showWarningModal(inv, res.message, res.shortageList);
      } else {
        message.success("Inventory updated successfully");
        await refetch();
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to update inventory");
    }
  };

  const showWarningModal = (inv, messageText, shortageList) => {
    Modal.warning({
      title: "Warning",
      icon: <ExclamationCircleOutlined />,
      width: 600,
      content: (
        <div>
          <p>{messageText}</p>
          <ul>
            {shortageList.map((item, idx) => (
              <li key={idx}>
                <strong>{item.name}</strong> — parts left: {item.partsLeft}
              </li>
            ))}
          </ul>
          <Button
            type="primary"
            danger
            onClick={() => handleInventorySelect(inv, inv.categoryName, true)}
          >
            Force Skip & Proceed
          </Button>
        </div>
      ),
    });
  };

  const handleCompleteScan = async () => {
    if (!allScanned) {
      Modal.info({
        title: "Incomplete",
        content: "Please scan all inventories before completing the scan.",
      });
      return;
    }

    Modal.confirm({
      title: "Complete Scan?",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to mark inventory verification as complete for this order? This will update booking verification state.",
      okText: "Yes, Complete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await verifyInventory({ variables: { data: orderId } });
          const out = res?.data?.verifyInventory;
          if (!out) throw new Error("Invalid server response");

          if (out.status === "ERROR") {
            Modal.error({ title: "Error", content: out.message });
            return;
          }

          // If backend returns order, prefer it to update UI / parent
          const updatedOrder = out.order || null;

          message.success(out.message || "Inventory verification completed");

          // refetch inventories to keep UI in sync
          await refetch();

          // optionally notify parent (e.g. BookingForm) to update its order state
          if (typeof onUpdate === "function" && updatedOrder) {
            onUpdate(updatedOrder);
          }
        } catch (err) {
          console.error("verifyInventory failed:", err);
          Modal.error({
            title: "Verification failed",
            content: err.message || "Failed to verify inventory",
          });
        }
      },
    });
  };

  // Entry point for the "Generate Inventories" button. Mirrors the employee
  // app's start-service flow: ask about package selections first (if any
  // ON_SELECTION service on this order needs it), then ask about manual
  // counts (e.g. Self Disposable Packet - how many people were served),
  // and only then actually generate.
  const handleGenerateInventories = async () => {
    setGenerating(true);
    try {
      const { data: svcData } = await fetchOrderServicesForInventory({
        variables: { data: { orderId } },
      });
      const orderServices =
        svcData?.getOrderServicesForInventory?.orderServices || [];

      const needsSelection = orderServices.filter(
        (os) =>
          os.serviceDetails?.inventoryGenerationMode === "ON_SELECTION" &&
          (os.serviceDetails?.selectionCount || 0) > 0
      );

      if (needsSelection.length > 0) {
        setPackagesNeedingSelection(needsSelection);
        setPackageSelections(
          Object.fromEntries(needsSelection.map((os) => [os.id, []]))
        );
        setPackageModalOpen(true);
        setGenerating(false);
        return;
      }

      await proceedToManualCountStep([]);
    } catch (err) {
      console.error("Failed to prepare inventory generation", err);
      message.error("Failed to load order services");
      setGenerating(false);
    }
  };

  // Called once package selections are confirmed (or skipped, as []).
  const proceedToManualCountStep = async (selectedPackagesPayload) => {
    setGenerating(true);
    try {
      const { data: catData } = await fetchManualCountCategories();
      const categories = (
        catData?.getInventoryCategory?.categories || []
      ).filter((c) => c.allowManualCount);

      if (categories.length > 0) {
        setManualCountCategories(categories);
        setManualCounts(
          Object.fromEntries(categories.map((c) => [c.id, 1]))
        );
        setManualCountModalOpen(true);
        // stash the package payload until the manual-count modal confirms
        setPendingPackagePayload(selectedPackagesPayload);
        setGenerating(false);
        return;
      }

      await submitGenerateInventories(selectedPackagesPayload, []);
    } catch (err) {
      console.error("Failed to load manual count categories", err);
      message.error("Failed to load inventory categories");
      setGenerating(false);
    }
  };

  const submitGenerateInventories = async (
    selectedPackagesPayload,
    manualInventoryCountsPayload
  ) => {
    setGenerating(true);
    try {
      const res = await generateOrderInventories({
        variables: {
          data: {
            orderId,
            selectedPackages: selectedPackagesPayload,
            manualInventoryCounts: manualInventoryCountsPayload,
          },
        },
      });

      const payload = res.data?.generateOrderInventories;
      if (!payload) throw new Error("Invalid response from server");

      if (payload.status === "ERROR") {
        Modal.error({ title: "Error", content: payload.message });
      } else {
        message.success(payload.message || "Inventories generated");
        // If inventories returned, update UI
        const inventories = payload.inventories || [];
        if (inventories.length > 0) {
          setGroupedData(groupInventories(inventories));
          setAllInventories(inventories);
          setInventoriesEmpty(false);
          const allDone = inventories.every((inv) => inv.scanned);
          setAllScanned(allDone);
        }

        // refetch original query to keep cache in sync
        await refetch();
      }
    } catch (err) {
      console.error("Failed to generate inventories", err);
      message.error("Failed to generate inventories");
    } finally {
      setGenerating(false);
    }
  };

  const togglePackageSelection = (orderServiceId, childId, maxCount) => {
    setPackageSelections((prev) => {
      const current = prev[orderServiceId] || [];
      const isSelected = current.includes(childId);
      let next;
      if (isSelected) {
        next = current.filter((id) => id !== childId);
      } else {
        if (current.length >= maxCount) return prev; // cap reached, ignore
        next = [...current, childId];
      }
      return { ...prev, [orderServiceId]: next };
    });
  };

  const handlePackageModalCancel = () => {
    setPackageModalOpen(false);
    setPackagesNeedingSelection([]);
  };

  const handlePackageModalConfirm = () => {
    const incomplete = packagesNeedingSelection.some(
      (os) =>
        (packageSelections[os.id] || []).length !==
        (os.serviceDetails?.selectionCount || 0)
    );
    if (incomplete) {
      message.warning("Please complete all package selections before continuing.");
      return;
    }
    const payload = packagesNeedingSelection.map((os) => ({
      orderServiceId: os.id,
      selectedServiceIds: packageSelections[os.id] || [],
    }));
    setPackageModalOpen(false);
    proceedToManualCountStep(payload);
  };

  const handleManualCountModalCancel = () => {
    setManualCountModalOpen(false);
    setManualCountCategories([]);
    setGenerating(false);
  };

  const handleManualCountModalConfirm = () => {
    const payload = manualCountCategories.map((c) => ({
      categoryId: c.id,
      count: manualCounts[c.id] || 1,
    }));
    setManualCountModalOpen(false);
    submitGenerateInventories(pendingPackagePayload, payload);
  };

  // --- Edit mode helpers ---

  const resetEditState = () => {
    setEditedParts({});
    setToggledMarks({});
    setStagedAdds([]);
    setAddItemDrafts({});
    setBatchReason("");
  };

  const toggleEditMode = () => {
    if (editMode) resetEditState();
    setEditMode((prev) => !prev);
  };

  const stageQuantityChange = (inv, newParts) => {
    setEditedParts((prev) => {
      const next = { ...prev };
      if (!newParts || newParts === inv.parts) {
        delete next[inv.id];
      } else {
        next[inv.id] = newParts;
      }
      return next;
    });
  };

  const toggleMark = (inv) => {
    setToggledMarks((prev) => {
      const next = { ...prev };
      const desired = inv.reversed ? "RESTORE" : "REVERSE";
      if (next[inv.id] === desired) {
        delete next[inv.id];
      } else {
        next[inv.id] = desired;
      }
      return next;
    });
  };

  const stageAddItem = (serviceKey, serviceId, serviceLabel) => {
    const draft = addItemDrafts[serviceKey];
    const category = addItemCategories.find((c) => c.id === draft?.categoryId);
    if (!category) {
      message.warning("Pick a category to add first.");
      return;
    }
    const count = draft.count && draft.count > 0 ? draft.count : 1;
    setTempIdCounter((c) => c + 1);
    setStagedAdds((prev) => [
      ...prev,
      {
        tempId: `add-${tempIdCounter}-${category.id}`,
        serviceId: serviceId || null,
        serviceLabel,
        categoryId: category.id,
        categoryName: category.name,
        count,
      },
    ]);
    setAddItemDrafts((prev) => ({ ...prev, [serviceKey]: { categoryId: null, count: 1 } }));
  };

  const unstageAdd = (tempId) => {
    setStagedAdds((prev) => prev.filter((a) => a.tempId !== tempId));
  };

  const pendingEditEntries = useMemo(() => {
    const entries = [];
    allInventories.forEach((inv) => {
      if (editedParts[inv.id] !== undefined) {
        entries.push({
          key: `parts-${inv.id}`,
          label: `${inv.categoryName}: quantity ${inv.parts} → ${editedParts[inv.id]}`,
          onRemove: () => stageQuantityChange(inv, inv.parts),
        });
      }
      if (toggledMarks[inv.id] === "REVERSE") {
        entries.push({
          key: `reverse-${inv.id}`,
          label: `${inv.categoryName}: mark not needed${inv.scanned ? " (credit back)" : ""}`,
          onRemove: () => toggleMark(inv),
        });
      }
      if (toggledMarks[inv.id] === "RESTORE") {
        entries.push({
          key: `restore-${inv.id}`,
          label: `${inv.categoryName}: restore`,
          onRemove: () => toggleMark(inv),
        });
      }
    });
    stagedAdds.forEach((a) => {
      entries.push({
        key: a.tempId,
        label: `Add: ${a.categoryName} × ${a.count}${a.serviceLabel ? ` (${a.serviceLabel})` : ""}`,
        onRemove: () => unstageAdd(a.tempId),
      });
    });
    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInventories, editedParts, toggledMarks, stagedAdds]);

  const handleSubmitEdits = async () => {
    if (!batchReason.trim()) {
      message.warning("Please enter a reason for these changes.");
      return;
    }
    setSubmittingEdits(true);
    const errors = [];

    try {
      // Quantity edits — one row at a time (each can fail independently).
      for (const [orderInventoryId, parts] of Object.entries(editedParts)) {
        try {
          const res = await adminUpdateOrderInventoryParts({
            variables: { data: { orderInventoryId, parts, reason: batchReason } },
          });
          if (res.data?.adminUpdateOrderInventoryParts?.status === "ERROR") {
            errors.push(res.data.adminUpdateOrderInventoryParts.message);
          }
        } catch (err) {
          errors.push(err.message || "Failed to update a quantity");
        }
      }

      // Mark-not-needed - batched into a single call.
      const reverseIds = Object.entries(toggledMarks)
        .filter(([, v]) => v === "REVERSE")
        .map(([id]) => id);
      if (reverseIds.length > 0) {
        try {
          const res = await adminReverseOrderInventory({
            variables: {
              data: { orderId, orderInventoryIds: reverseIds, reason: batchReason },
            },
          });
          if (res.data?.adminReverseOrderInventory?.status === "ERROR") {
            errors.push(res.data.adminReverseOrderInventory.message);
          }
        } catch (err) {
          errors.push(err.message || "Failed to mark items not needed");
        }
      }

      // Restores — one at a time (no batch mutation exists for this).
      const restoreIds = Object.entries(toggledMarks)
        .filter(([, v]) => v === "RESTORE")
        .map(([id]) => id);
      for (const orderInventoryId of restoreIds) {
        try {
          const res = await adminRestoreOrderInventoryItem({
            variables: { data: { orderInventoryId, reason: batchReason } },
          });
          if (res.data?.adminRestoreOrderInventoryItem?.status === "ERROR") {
            errors.push(res.data.adminRestoreOrderInventoryItem.message);
          }
        } catch (err) {
          errors.push(err.message || "Failed to restore an item");
        }
      }

      // Added items — one at a time (each may differ in category/service/count).
      for (const add of stagedAdds) {
        try {
          const res = await adminAddOrderInventoryItem({
            variables: {
              data: {
                orderId,
                serviceId: add.serviceId,
                categoryId: add.categoryId,
                count: add.count,
                reason: batchReason,
              },
            },
          });
          if (res.data?.adminAddOrderInventoryItem?.status === "ERROR") {
            errors.push(res.data.adminAddOrderInventoryItem.message);
          }
        } catch (err) {
          errors.push(err.message || "Failed to add an item");
        }
      }

      await refetch();

      if (errors.length > 0) {
        Modal.error({
          title: "Some changes failed",
          content: (
            <ul>
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          ),
        });
      } else {
        message.success("Changes saved.");
      }

      setReviewModalOpen(false);
      resetEditState();
      setEditMode(false);
    } finally {
      setSubmittingEdits(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (
    !editingData.verificationStatus ||
    editingData.verificationStatus === "PENDING"
  ) {
    return (
      <div className="bg-[#1a1a1a] text-white p-6 rounded-md text-center">
        <h3 className="text-[#FBB034] text-lg font-bold mb-3">
          Verification Status
        </h3>
        <p className="text-xl font-semibold mb-2">
          Current Status: {editingData.verificationStatus}
        </p>
        <p className="text-gray-400 text-sm">
          Verification is in progress or already completed. You cannot modify
          this booking now.
        </p>
      </div>
    );
  }

  const renderStatus = (inv) => {
    if (inv.reversed) {
      return (
        <span className="text-purple-400 font-semibold block text-right">
          {REVERSED_REASON_LABELS[inv.reversedReason] || "Credited back"}
          <span className="block text-[10px] text-gray-500 font-normal">
            {inv.reversedReason}
          </span>
        </span>
      );
    }
    if (inv.deductionGap) {
      return (
        <span className="text-orange-400 font-semibold block text-right">
          Not deducted
          <span className="block text-[10px] font-normal">
            {{
              NO_STOCK: "No stock",
              INSUFFICIENT_STOCK: "Not enough stock",
              NOT_SCANNED: "Not scanned",
            }[inv.deductionGap] || inv.deductionGap}
          </span>
          {inv.deductionGapDetail && (
            <span className="block text-[10px] text-gray-400 font-normal max-w-[180px]">
              {inv.deductionGapDetail}
            </span>
          )}
        </span>
      );
    }
    if (inv.scanned) {
      return <span className="text-green-400 font-semibold">Scanned</span>;
    }
    if (inv.isScanRequired === false) {
      return <span className="text-blue-400 font-semibold">Auto Cut</span>;
    }
    return <span className="text-amber-400 font-semibold">Pending</span>;
  };

  return (
    <div className="p-6 bg-[#121212] min-h-screen text-white">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-[#FBB033]">
          Order Inventory Details
        </h2>
        {!inventoriesEmpty && (
          <Button
            icon={<EditOutlined />}
            onClick={toggleEditMode}
            disabled={verifying || submittingEdits}
          >
            {editMode ? "Cancel Editing" : "Edit Inventory"}
          </Button>
        )}
      </div>

      {!inventoriesEmpty && inventoryByCategory.length > 0 && (
        <Card className="mb-4 bg-[#1e1e1e] text-white" bodyStyle={{ backgroundColor: "#1e1e1e" }}>
          <h3 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide mb-2">
            Expected vs Deducted (by category)
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-gray-400 border-b border-[#333]">
                <tr>
                  <th className="py-1.5 pr-4 font-medium">Category</th>
                  <th className="py-1.5 pr-4 font-medium">Should Deduct</th>
                  <th className="py-1.5 pr-4 font-medium">Actually Deducted</th>
                  <th className="py-1.5 pr-4 font-medium">Gap</th>
                  <th className="py-1.5 pr-4 font-medium">Extra Cost</th>
                </tr>
              </thead>
              <tbody>
                {inventoryByCategory.map((cat) => {
                  const gap = cat.expectedParts - cat.actualParts;
                  return (
                    <tr key={cat.categoryId} className="border-b border-[#333]">
                      <td className="py-1.5 pr-4 text-white">{cat.categoryName}</td>
                      <td className="py-1.5 pr-4 text-gray-300">{cat.expectedParts}</td>
                      <td className="py-1.5 pr-4 text-gray-300">{cat.actualParts}</td>
                      <td
                        className={`py-1.5 pr-4 font-semibold ${
                          gap === 0 ? "text-green-400" : "text-amber-400"
                        }`}
                      >
                        {gap === 0 ? "None" : gap}
                      </td>
                      <td className="py-1.5 pr-4 text-gray-300">
                        {cat.extraCost > 0 ? `₹${cat.extraCost}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {inventoriesEmpty && (
        <Card className="mb-4 bg-[#1e1e1e] text-white">
          <p className="mb-3">No inventories found for this order.</p>
          <Button
            type="primary"
            onClick={handleGenerateInventories}
            loading={generating}
          >
            Generate Inventories
          </Button>
        </Card>
      )}

      {Object.keys(groupedData).map((key) => {
        const rows = groupedData[key];
        const serviceId = rows[0]?.serviceId;
        const draft = addItemDrafts[key] || { categoryId: null, count: 1 };
        return (
          <Card
            key={key}
            title={<span className="text-[#FBB033] font-semibold">{key}</span>}
            className="mb-4 bg-[#1e1e1e] text-white"
            headStyle={{
              backgroundColor: "#1e1e1e",
              borderBottom: "1px solid #333",
            }}
            bodyStyle={{ backgroundColor: "#1e1e1e" }}
          >
            {rows.map((inv) => {
              const canEditParts = !inv.scanned && !inv.reversed;
              const markedReverse = toggledMarks[inv.id] === "REVERSE";
              const markedRestore = toggledMarks[inv.id] === "RESTORE";
              return (
                <div
                  key={inv.id}
                  className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 py-2 border-b border-gray-700"
                >
                  <div>
                    <div>{inv.categoryName}</div>
                    <div className="text-gray-400 text-xs">
                      {inv.productSelectionMode === "ALTERNATE_FALLBACK" && (
                        <span className="text-amber-400">Alternate not chosen</span>
                      )}
                      {inv.productSelectionMode === "EMPLOYEE_OVERRIDDEN_COUNT" && (
                        <span className="text-amber-400">
                          Exceeded recipe default ({inv.recipeDefaultCount})
                          {inv.extraCost > 0 ? ` +₹${inv.extraCost}` : ""}
                        </span>
                      )}
                      {inv.productSelectionMode === "EMPLOYEE_ADDED_ADHOC" && (
                        <span className="text-amber-400">
                          Added by employee, not in recipe
                          {inv.extraCost > 0 ? ` +₹${inv.extraCost}` : ""}
                        </span>
                      )}
                      {inv.productSelectionMode === "ADMIN_ADDED_ADHOC" && (
                        <span className="text-amber-400">Added by admin</span>
                      )}
                    </div>
                    {inv.scannedInventoryDetails && (
                      <div className="text-gray-500 text-[11px]">
                        Physical unit status: {inv.scannedInventoryDetails.status}
                        {" · usage "}
                        {inv.scannedInventoryDetails.usage}/
                        {inv.scannedInventoryDetails.quantity}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    {editMode ? (
                      <div className="flex items-center gap-2">
                        {canEditParts ? (
                          <InputNumber
                            min={1}
                            size="small"
                            value={editedParts[inv.id] ?? inv.parts}
                            onChange={(val) => stageQuantityChange(inv, val)}
                            style={{ width: 70 }}
                          />
                        ) : (
                          <span className="text-gray-500 text-xs">
                            parts: {inv.parts}
                          </span>
                        )}
                        {!inv.reversed && (
                          <Button
                            size="small"
                            danger={!markedReverse}
                            type={markedReverse ? "primary" : "default"}
                            onClick={() => toggleMark(inv)}
                          >
                            {markedReverse
                              ? "Undo"
                              : inv.scanned
                              ? "Credit back"
                              : "Mark not needed"}
                          </Button>
                        )}
                        {inv.reversed && (
                          <Button
                            size="small"
                            type={markedRestore ? "primary" : "default"}
                            onClick={() => toggleMark(inv)}
                          >
                            {markedRestore ? "Undo" : "Restore"}
                          </Button>
                        )}
                      </div>
                    ) : (
                      renderStatus(inv)
                    )}

                    {!editMode && !inv.reversed && !inv.scanned && inv.isScanRequired !== false && (
                      <Select
                        showSearch
                        placeholder="Search or select inventory"
                        className="w-full sm:w-[300px]"
                        style={{ width: undefined }}
                        onChange={(val) => handleInventorySelect(inv, val)}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label || "")
                            .toLowerCase()
                            .includes(String(input).toLowerCase())
                        }
                        allowClear
                        notFoundContent={<span>No matching inventory</span>}
                        options={availableInventories
                          .filter(
                            (item) => item.inventoryCategory?.id === inv.inventoryId
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((item) => ({
                            value: item.name,
                            label: item.name,
                          }))}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {editMode && (
              <div className="flex items-center gap-2 pt-3 mt-2 border-t border-gray-700">
                <Select
                  showSearch
                  placeholder="+ Add missing item"
                  size="small"
                  style={{ width: 220 }}
                  value={draft.categoryId || undefined}
                  optionFilterProp="label"
                  onChange={(val) =>
                    setAddItemDrafts((prev) => ({
                      ...prev,
                      [key]: { ...draft, categoryId: val },
                    }))
                  }
                  options={addItemCategories.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                />
                <InputNumber
                  min={1}
                  size="small"
                  value={draft.count}
                  style={{ width: 70 }}
                  onChange={(val) =>
                    setAddItemDrafts((prev) => ({
                      ...prev,
                      [key]: { ...draft, count: val || 1 },
                    }))
                  }
                />
                <Button size="small" onClick={() => stageAddItem(key, serviceId, key)}>
                  Stage
                </Button>
              </div>
            )}
          </Card>
        );
      })}

      {editMode && pendingEditEntries.length > 0 && (
        <div className="flex justify-center mb-4">
          <Button type="primary" onClick={() => setReviewModalOpen(true)}>
            Review {pendingEditEntries.length} change(s)
          </Button>
        </div>
      )}

      <div className="flex flex-col items-center mt-6">
        <Button
          type="primary"
          size="large"
          className="bg-green-500 border-none"
          disabled={!allScanned || verifying || editMode}
          loading={verifying}
          onClick={handleCompleteScan}
        >
          Complete Scan
        </Button>
      </div>

      <Modal
        title="Review changes"
        open={reviewModalOpen}
        onCancel={() => setReviewModalOpen(false)}
        okText={submittingEdits ? "Saving…" : "Confirm & Save"}
        onOk={handleSubmitEdits}
        okButtonProps={{ loading: submittingEdits, disabled: !batchReason.trim() }}
        cancelButtonProps={{ disabled: submittingEdits }}
      >
        <ul className="mb-3 list-disc pl-5">
          {pendingEditEntries.map((e) => (
            <li key={e.key} className="flex items-center justify-between gap-2 mb-1">
              <span>{e.label}</span>
              <Button size="small" type="link" danger onClick={e.onRemove} disabled={submittingEdits}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
        <p className="mb-1 text-sm font-medium">Reason for these changes</p>
        <Input.TextArea
          rows={2}
          value={batchReason}
          onChange={(e) => setBatchReason(e.target.value)}
          placeholder="Why are you making this correction?"
          disabled={submittingEdits}
        />
      </Modal>

      <Modal
        title="Select Package Services"
        open={packageModalOpen}
        onOk={handlePackageModalConfirm}
        onCancel={handlePackageModalCancel}
        okText="Confirm & Generate"
        maskClosable={false}
      >
        {packagesNeedingSelection.map((os) => {
          const selectionCount = os.serviceDetails?.selectionCount || 0;
          const selected = packageSelections[os.id] || [];
          return (
            <div key={os.id} className="mb-4">
              <p className="font-semibold mb-1">
                {os.serviceDetails?.name} — select {selectionCount} (
                {selected.length}/{selectionCount})
              </p>
              {(os.serviceDetails?.packageChildServices || []).map((child) => (
                <div key={child.id}>
                  <Checkbox
                    checked={selected.includes(child.id)}
                    onChange={() =>
                      togglePackageSelection(os.id, child.id, selectionCount)
                    }
                  >
                    {child.name}
                    {child.price ? ` — ₹${child.price}` : ""}
                  </Checkbox>
                </div>
              ))}
            </div>
          );
        })}
      </Modal>

      <Modal
        title="How many people were served?"
        open={manualCountModalOpen}
        onOk={handleManualCountModalConfirm}
        onCancel={handleManualCountModalCancel}
        okText="Confirm & Generate"
        maskClosable={false}
      >
        <p className="text-gray-500 mb-3">
          Enter a count for each item below.
        </p>
        {manualCountCategories.map((c) => (
          <div
            key={c.id}
            className="flex justify-between items-center mb-3"
          >
            <span>{c.name}</span>
            <InputNumber
              min={1}
              value={manualCounts[c.id] || 1}
              onChange={(val) =>
                setManualCounts((prev) => ({ ...prev, [c.id]: val || 1 }))
              }
            />
          </div>
        ))}
      </Modal>
    </div>
  );
};

export default OrderInventoryPage;
